import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Space,
  Typography,
  message,
} from 'antd';
import {
  ClockCircleOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import {
  getServiceTimeConfig,
  type ServiceTimeSlot,
  updateServiceTimeConfig,
} from '../../services/serviceTimeConfigService';

interface ServiceTimeConfigFormValues {
  bookingDays: number;
  timeSlots: ServiceTimeSlot[];
}

const createEmptySlot = (): ServiceTimeSlot => ({
  startTime: '',
  endTime: '',
});

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const parseMinutes = (value: string) => {
  if (!TIME_PATTERN.test(value)) {
    return null;
  }
  const [hour, minute] = value.split(':').map(Number);
  return hour * 60 + minute;
};

const sortSlots = (slots: ServiceTimeSlot[]) => {
  return [...slots].sort((left, right) => {
    const leftMinutes = parseMinutes(left.startTime) ?? Number.MAX_SAFE_INTEGER;
    const rightMinutes = parseMinutes(right.startTime) ?? Number.MAX_SAFE_INTEGER;
    if (leftMinutes === rightMinutes) {
      return (parseMinutes(left.endTime) ?? Number.MAX_SAFE_INTEGER) - (parseMinutes(right.endTime) ?? Number.MAX_SAFE_INTEGER);
    }
    return leftMinutes - rightMinutes;
  });
};

const validateTimeSlots = (_: unknown, value?: ServiceTimeSlot[]) => {
  const slots = (value || []).map((item) => ({
    startTime: item?.startTime?.trim() || '',
    endTime: item?.endTime?.trim() || '',
  }));

  if (!slots.length) {
    return Promise.reject(new Error('请至少配置一个服务时间段'));
  }

  const normalized = sortSlots(slots);
  let previousEndMinutes: number | null = null;

  for (let index = 0; index < normalized.length; index += 1) {
    const slot = normalized[index];
    if (!slot.startTime || !slot.endTime) {
      return Promise.reject(new Error(`第 ${index + 1} 个服务时间段不能为空`));
    }

    const startMinutes = parseMinutes(slot.startTime);
    if (startMinutes === null) {
      return Promise.reject(new Error(`第 ${index + 1} 个服务时间段开始时间格式不正确，请使用 HH:mm`));
    }

    const endMinutes = parseMinutes(slot.endTime);
    if (endMinutes === null) {
      return Promise.reject(new Error(`第 ${index + 1} 个服务时间段结束时间格式不正确，请使用 HH:mm`));
    }

    if (startMinutes >= endMinutes) {
      return Promise.reject(new Error(`第 ${index + 1} 个服务时间段开始时间必须早于结束时间`));
    }

    if (previousEndMinutes !== null && startMinutes < previousEndMinutes) {
      return Promise.reject(new Error('服务时间段不能重叠'));
    }

    previousEndMinutes = endMinutes;
  }

  return Promise.resolve();
};

const ServiceTimeConfigPage: React.FC = () => {
  const [form] = Form.useForm<ServiceTimeConfigFormValues>();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const watchedSlots = Form.useWatch('timeSlots', form);
  const watchedBookingDays = Form.useWatch('bookingDays', form);

  const previewSlots = useMemo(() => {
    const slots = watchedSlots || [];
    return sortSlots(
      slots
        .map((item) => ({
          startTime: item?.startTime?.trim() || '',
          endTime: item?.endTime?.trim() || '',
        }))
        .filter((item) => item.startTime && item.endTime),
    );
  }, [watchedSlots]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getServiceTimeConfig();
      form.setFieldsValue({
        bookingDays: res.data?.bookingDays || 21,
        timeSlots: res.data?.timeSlots?.length ? res.data.timeSlots : [createEmptySlot()],
      });
    } catch {
      message.error('加载服务时间配置失败');
      form.setFieldsValue({
        bookingDays: 21,
        timeSlots: [createEmptySlot()],
      });
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);
      const payload = {
        bookingDays: values.bookingDays,
        timeSlots: sortSlots(
          (values.timeSlots || []).map((item) => ({
            startTime: item.startTime.trim(),
            endTime: item.endTime.trim(),
          })),
        ),
      };
      await updateServiceTimeConfig(payload);
      message.success('保存成功');
      form.setFieldsValue({
        bookingDays: payload.bookingDays,
        timeSlots: payload.timeSlots,
      });
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.response?.data?.message || '保存服务时间配置失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        loading={loading}
        title={(
          <Space size={8}>
            <ClockCircleOutlined />
            <span>服务时间管理</span>
          </Space>
        )}
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void loadData()}>
              刷新
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={submitLoading} onClick={() => void handleSubmit()}>
              保存配置
            </Button>
          </Space>
        )}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
          统一配置用户端可预约未来天数，以及每天固定可选的服务时间段。
        </Typography.Paragraph>

        <Form<ServiceTimeConfigFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            bookingDays: 21,
            timeSlots: [createEmptySlot()],
          }}
        >
          <Form.Item
            label="可预约未来天数"
            name="bookingDays"
            rules={[
              { required: true, message: '请输入可预约未来天数' },
              { type: 'number', min: 1, max: 365, message: '可预约未来天数需在 1 到 365 之间' },
            ]}
            extra="用户端预约页面将按此配置生成可选日期范围。"
          >
            <InputNumber min={1} max={365} precision={0} style={{ width: 220 }} placeholder="请输入天数" />
          </Form.Item>

          <Form.Item label="每日固定服务时间段" required>
            <Form.Item name="timeSlots" noStyle rules={[{ validator: validateTimeSlots }]}>
              <div />
            </Form.Item>
            <Form.List name="timeSlots">
              {(fields, { add, remove }) => (
                <Space orientation="vertical" size={16} style={{ width: '100%' }}>
                  {fields.map((field, index) => (
                    <Space key={field.key} align="start" wrap>
                      <Form.Item
                        {...field}
                        label={index === 0 ? '开始时间' : ' '}
                        name={[field.name, 'startTime']}
                        style={{ marginBottom: 0 }}
                        rules={[
                          { required: true, message: '请输入开始时间' },
                          { pattern: TIME_PATTERN, message: '请使用 HH:mm' },
                        ]}
                      >
                        <Input placeholder="例如 09:00" style={{ width: 160 }} maxLength={5} />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        label={index === 0 ? '结束时间' : ' '}
                        name={[field.name, 'endTime']}
                        style={{ marginBottom: 0 }}
                        rules={[
                          { required: true, message: '请输入结束时间' },
                          { pattern: TIME_PATTERN, message: '请使用 HH:mm' },
                        ]}
                      >
                        <Input placeholder="例如 12:00" style={{ width: 160 }} maxLength={5} />
                      </Form.Item>
                      <Form.Item label={index === 0 ? '操作' : ' '} style={{ marginBottom: 0 }}>
                        <Button
                          danger
                          icon={<MinusCircleOutlined />}
                          disabled={fields.length <= 1}
                          onClick={() => remove(field.name)}
                        >
                          删除
                        </Button>
                      </Form.Item>
                    </Space>
                  ))}
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add(createEmptySlot())}>
                    新增时间段
                  </Button>
                </Space>
              )}
            </Form.List>
          </Form.Item>
        </Form>

        <Card size="small" title="当前预览" style={{ marginTop: 24, background: '#fafafa' }}>
          <Space orientation="vertical" size={8}>
            <Typography.Text>
              可预约未来天数：{watchedBookingDays || 21} 天
            </Typography.Text>
            <Typography.Text>
              时间段：{previewSlots.length ? previewSlots.map((item) => `${item.startTime}-${item.endTime}`).join('、') : '暂未配置'}
            </Typography.Text>
          </Space>
        </Card>
      </Card>
    </div>
  );
};

export default ServiceTimeConfigPage;
